'use client';

import { c as _c } from "react/compiler-runtime";
import { CssBaseline, ThemeProvider as MuiThemeProvider, extendTheme, useColorScheme, type PaletteMode } from '@mui/material';
import { AppRouterCacheProvider } from '@mui/material-nextjs/v16-appRouter';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { createContext, useCallback, useEffect, useMemo, type PropsWithChildren } from 'react';
import type {} from '@mui/material/themeCssVarsAugmentation'; //! Do not remove this import otherwise you will get type errors while making a production build

import { themeConfig } from '@/configs/themeConfig';
import { useNonProjectDal } from '@/contexts/useNonProjectDal';
import { PreferencesKey } from '@/data/dal/preferencesKey';
import { theme as defaultCoreTheme } from '@/theme';
export const ThemeContext = createContext<{
  theme: PaletteMode;
  changeTheme: (theme: PaletteMode) => void;
}>({
  theme: 'light',
  changeTheme: () => {}
});
export const ThemeProvider = props => {
  const $ = _c(4);
  if (props.isAuthenticated) {
    let t0;
    if ($[0] !== props.children) {
      t0 = <AuthThemeProvider>{props.children}</AuthThemeProvider>;
      $[0] = props.children;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  let t0;
  if ($[2] !== props.children) {
    t0 = <InnerThemeProvider currentMode="light" onChangeTheme={_temp}>{props.children}</InnerThemeProvider>;
    $[2] = props.children;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
};
const AuthThemeProvider = (props: PropsWithChildren) => {
  const {
    nonProjectDal: dal
  } = useNonProjectDal();
  const {
    data: prefResponse
  } = dal.preferences.useUserPref(PreferencesKey.Preferences);
  const currentMode: PaletteMode = prefResponse?.value?.theme || 'light';
  return <InnerThemeProvider currentMode={currentMode} onChangeTheme={t => {
    void dal.preferences.updateUserPref(PreferencesKey.Preferences, {
      ...prefResponse?.value,
      theme: t
    });
  }}>
      {props.children}
    </InnerThemeProvider>;
};
const InnerThemeProvider = props => {
  const $ = _c(8);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    const coreTheme = defaultCoreTheme();
    t0 = extendTheme(coreTheme);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const theme = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      prepend: true
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = themeConfig.templateName.toLowerCase().split(" ").join("-");
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <CssBaseline />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== props.children || $[5] !== props.currentMode || $[6] !== props.onChangeTheme) {
    t4 = <AppRouterCacheProvider options={t1}><MuiThemeProvider theme={theme} defaultMode="light" modeStorageKey={`${t2}-mui-template-mode`}><LocalizationProvider dateAdapter={AdapterDayjs}><ThemeWrapper theme={props.currentMode} onChangeTheme={props.onChangeTheme}>{t3}{props.children}</ThemeWrapper></LocalizationProvider></MuiThemeProvider></AppRouterCacheProvider>;
    $[4] = props.children;
    $[5] = props.currentMode;
    $[6] = props.onChangeTheme;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
const ThemeWrapper = ({
  children,
  theme,
  onChangeTheme
}: PropsWithChildren<{
  theme: PaletteMode;
  onChangeTheme: (t: PaletteMode) => void;
}>) => {
  const {
    mode,
    setMode
  } = useColorScheme();
  useEffect(() => {
    setMode(theme);
    // setMode is intentionally excluded from deps — MUI can return a new
    // function reference on each render, which would create a tight loop where
    // calling setMode() causes a re-render → new setMode ref → effect fires
    // again → setMode() called again → visual flickering of the CSS class on
    // <html>. The effect should only re-run when the user's theme preference
    // changes, not when MUI's internal provider re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);
  const changeTheme = useCallback((t: PaletteMode) => {
    setMode(t);
    onChangeTheme(t);
  }, [setMode, onChangeTheme]);
  const contextValue = useMemo<{
    theme: PaletteMode;
    changeTheme: (t: PaletteMode) => void;
  }>(() => ({
    theme: mode === 'dark' ? 'dark' : 'light',
    changeTheme
  }), [mode, changeTheme]);
  return <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>;
};
function _temp() {}